import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import InvoicePrintModal from '../InvoiceManagement/components/InvoicePrintModal';
import { getDefaultInvoicePrintCheckedColumns } from '../InvoiceManagement/constants/invoiceManagementConstants';
import { Box, useMediaQuery, useTheme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StyledContainer } from '../../../components/styled/CommonStyles';
import LoadingIndicator from '../../../components/common/LoadingIndicator';
import CustomAlert from '../../../components/common/CustomAlert';
import DownloadIndicator from '../../../components/common/DownloadIndicator';
import { useAuth } from '../../../hooks/useAuth';
import FilterSection from './components/FilterSection';
import InvoiceTable from './components/InvoiceTable';
import InvoiceMobileView from './components/InvoiceMobileView';
import TableControls from './components/TableControls';
import InvoiceIssuanceDialog from './components/InvoiceIssuanceDialog';
import SubOrderDetailDialog from '../OrderStatus/components/SubOrderDetailDialog';
import { setDownloadIndicatorRef } from '../../../utils/excelDownload';
import { useInvoiceState } from './hooks/useInvoiceState';
import { useInvoiceData } from './hooks/useInvoiceData';
import { useInvoiceHandlers } from './hooks/useInvoiceHandlers';
import { getBasicColumns } from './constants/invoiceConstants';
import { computeInvoiceSummary } from './utils/invoiceUtils';
import { exportDebitNote } from './utils/debitNoteExport';
import { useRestoredSearchState, useSearchStateCache } from '../../../utils/searchStateCache';

const InvoiceIssuance = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useAuth();
    const downloadIndicatorRef = useRef(null);

    const [subOrderDetailDialogOpen, setSubOrderDetailDialogOpen] = useState(false);
    const [selectedSubOrderId, setSelectedSubOrderId] = useState(null);
    const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
    const [invoicePreviewSelectedIds, setInvoicePreviewSelectedIds] = useState([]);
    const [invoicePreviewStubRows, setInvoicePreviewStubRows] = useState([]);

    const defaultPrintCheckedColumns = useMemo(() => getDefaultInvoicePrintCheckedColumns(t), [t]);

    const handleInvoicePreviewById = useCallback((invoiceId) => {
        const id = Number(invoiceId);
        if (!Number.isFinite(id)) return;
        setInvoicePreviewSelectedIds([id]);
        setInvoicePreviewStubRows([{ invoice_id: id }]);
        setInvoicePreviewOpen(true);
    }, []);

    const handleSubOrderClick = useCallback((subOrderId) => {
        if (subOrderId == null || subOrderId === '') return;
        setSelectedSubOrderId(subOrderId);
        setSubOrderDetailDialogOpen(true);
    }, []);

    const handleCloseSubOrderDialog = useCallback(() => {
        setSubOrderDetailDialogOpen(false);
        setSelectedSubOrderId(null);
    }, []);

    const cachedState = useRestoredSearchState('invoiceIssuance');

    const state = useInvoiceState(t, cachedState);
    const {
        searchParams,
        searchParamsRef,
        page,
        rowsPerPage,
        invoiceTableSortConfig,
        setInvoiceTableSortConfig,
        showMyOrderOnly,
        hideInvoicedRows,
        checkedColumns,
        columnPanelOpen,
        selectedItems,
        alertInfo,
        invoiceDialogOpen,
        hasSearched,

        setAlertInfo,
        setInvoiceDialogOpen,
        setSelectedItems,
        setSearchParams,
        setPage,
        setHasSearched,
        setShowMyOrderOnly,
        setHideInvoicedRows,

        handleParamChange,
        handleChangePage,
        handleChangeRowsPerPage,
        handleCheckColumn,
        toggleColumnPanel,
        selectAllColumns,
        unselectAllColumns,
        handleSelectItem,
        handleSelectAll,
        handleCloseAlert
    } = state;

    const printCheckedColumns = useMemo(() => ({
        ...defaultPrintCheckedColumns,
        manager_name: checkedColumns?.manager === true,
        factory: checkedColumns?.delivery === true,
        buyer: checkedColumns?.buyer !== false,
        style_no: checkedColumns?.style !== false,
        file_no: checkedColumns?.file !== false,
        color: checkedColumns?.color !== false,
        price: checkedColumns?.price !== false,
        amount: checkedColumns?.priceSum !== false,
        qty: checkedColumns?.orderQty !== false,
        sign: checkedColumns?.sign !== false
    }), [
        defaultPrintCheckedColumns,
        checkedColumns?.manager,
        checkedColumns?.delivery,
        checkedColumns?.buyer,
        checkedColumns?.style,
        checkedColumns?.file,
        checkedColumns?.color,
        checkedColumns?.price,
        checkedColumns?.priceSum,
        checkedColumns?.orderQty,
        checkedColumns?.sign
    ]);

    const [searchParamsToSend, setSearchParamsToSend] = useState(() => cachedState?.searchParamsToSend ?? searchParams);
    const [needToFetch, setNeedToFetch] = useState(false);

    const showMyOrderOnlyRef = useRef(showMyOrderOnly);
    const hideInvoicedRowsRef = useRef(hideInvoicedRows);
    const userIdRef = useRef(user?.user_id);
    useEffect(() => {
        showMyOrderOnlyRef.current = showMyOrderOnly;
    }, [showMyOrderOnly]);
    useEffect(() => {
        hideInvoicedRowsRef.current = hideInvoicedRows;
    }, [hideInvoicedRows]);
    useEffect(() => {
        userIdRef.current = user?.user_id;
    }, [user?.user_id]);

    const getInvoiceIssuanceListFetchSnapshot = useCallback(
        () => ({
            searchParams: searchParamsToSend,
            listOptions: {
                showMyOrderOnly: showMyOrderOnlyRef.current,
                hideInvoicedRows: hideInvoicedRowsRef.current,
                userId: userIdRef.current
            }
        }),
        [searchParamsToSend]
    );

    useEffect(() => {
        setDownloadIndicatorRef(downloadIndicatorRef);
    }, []);

    const {
        isLoading,
        isLoadingMore,
        isPackingLoading,
        isGiPostLoading,
        isInvoiceNoLoading,
        invoiceData,
        totalCount,
        hasMore,
        invoiceSummary,
        rawInvoiceData,
        fetchInvoiceData,
        loadMore,
        markInvoiceSearchForceRefresh,
        clearInvoiceIssuanceStorage,
        setInvoiceData,
        setTotalCount
    } = useInvoiceData(cachedState);

    useSearchStateCache(
        'invoiceIssuance',
        {
            searchParams,
            searchParamsToSend,
            invoiceData,
            rawInvoiceData,
            totalCount,
            hasMore,
            invoiceSummary,
            showMyOrderOnly,
            hideInvoicedRows,
            page,
            hasSearched: true
        },
        { enabled: hasSearched }
    );

    const handlers = useInvoiceHandlers(
        t,
        invoiceData,
        selectedItems,
        setAlertInfo,
        setInvoiceDialogOpen,
        setSelectedItems,
        fetchInvoiceData,
        markInvoiceSearchForceRefresh,
        setSearchParams,
        setPage,
        setInvoiceData,
        setTotalCount,
        setHasSearched,
        searchParamsRef,
        isLoading,
        setSearchParamsToSend,
        setInvoiceTableSortConfig,
        clearInvoiceIssuanceStorage,
        getInvoiceIssuanceListFetchSnapshot,
        totalCount,
        setNeedToFetch
    );

    const {
        handleSearch,
        handleReset,
        handleInvoiceIssuance,
        handleInvoiceConfirm,
        isIssuing
    } = handlers;

    const handleDebitNoteExport = useCallback(async () => {
        const selectedData = invoiceData.filter(item => selectedItems.includes(item.id));
        if (selectedData.length === 0) return;

        const vendorIds = new Set(
            selectedData.map(it => it.vendor_id ?? it.vendor_name ?? '').filter(v => v !== '')
        );
        if (vendorIds.size > 1) {
            const vendorNames = [];
            const seenVendors = new Set();
            selectedData.forEach(it => {
                const name = (it.vendor_name || '').trim();
                if (name && !seenVendors.has(name)) {
                    seenVendors.add(name);
                    vendorNames.push(name);
                }
            });
            const customContent = (
                <Box sx={{ textAlign: 'center', lineHeight: 1.7 }}>
                    <Typography sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
                        {t('invoice.debitNote.currentlySelectedCustomers')}
                    </Typography>
                    {vendorNames.map((name) => (
                        <Typography key={name} sx={{ color: 'text.primary' }}>
                            {name}
                        </Typography>
                    ))}
                </Box>
            );
            setAlertInfo({
                open: true,
                title: t('invoice.debitNote.errorTitle'),
                message: t('invoice.debitNote.differentCustomers'),
                customContent,
                severity: 'error',
                isConfirm: true
            });
            return;
        }

        const managers = [];
        const seen = new Set();
        selectedData.forEach(it => {
            const name = (it.manager_name || '').trim();
            if (name && !seen.has(name)) {
                seen.add(name);
                managers.push(name);
            }
        });

        const visibleColumns = getBasicColumns(t).filter(col => !col.meta && checkedColumns[col.id]);
        const includeSign = checkedColumns?.sign !== false;
        try {
            await exportDebitNote({
                items: selectedData,
                columns: visibleColumns,
                searchParams,
                managers,
                downloadIndicatorRef,
                includeSign
            });
        } catch (e) {
            console.error('거래 내역서 다운로드 실패:', e);
            setAlertInfo({
                open: true,
                title: t('error.title'),
                message: t('error.export'),
                severity: 'error'
            });
        }
    }, [invoiceData, selectedItems, searchParams, checkedColumns, setAlertInfo, t]);

    useEffect(() => {
        if (!needToFetch) return;
        if (!hasSearched) {
            setNeedToFetch(false);
            return;
        }
        setNeedToFetch(false);
        fetchInvoiceData(searchParamsToSend, setHasSearched, {
            showMyOrderOnly: showMyOrderOnlyRef.current,
            hideInvoicedRows: hideInvoicedRowsRef.current,
            userId: userIdRef.current
        }).catch((error) => {
            const status = error?.response?.status ?? error?.status ?? error?.code;
            const message =
                status === 400
                    ? t('invoice.error.invalidSearchParams', '검색 조건을 확인해 주세요.')
                    : t('error.fetch');
            setAlertInfo({
                open: true,
                title: t('error.title'),
                message,
                severity: 'error'
            });
        });
    }, [
        needToFetch,
        hasSearched,
        fetchInvoiceData,
        searchParamsToSend,
        setHasSearched,
        setAlertInfo,
        t
    ]);

    const columnsToUse = useMemo(() => getBasicColumns(t), [t]);

    const displaySummary = useMemo(() => {
        if (invoiceSummary) return invoiceSummary;
        return computeInvoiceSummary(invoiceData, i18n.language);
    }, [invoiceSummary, invoiceData, i18n.language]);

    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (e.key === 'F3' && !isLoading) {
                e.preventDefault();
                handleSearch();
            }
            if (e.key === 'F5' && !isLoading) {
                e.preventDefault();
                handleSearch();
            }
            if (e.key === 'F10' && !isLoading) {
                e.preventDefault();
                handleReset();
            }
            if (e.key === 'f' && e.ctrlKey && !isLoading) {
                e.preventDefault();
                handleSearch();
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setAlertInfo(prev => ({ ...prev, open: false }));
                setInvoiceDialogOpen(false);
                setSubOrderDetailDialogOpen(false);
                setInvoicePreviewOpen(false);
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [handleSearch, handleReset, isLoading, setAlertInfo, setInvoiceDialogOpen]);

    return (
        <StyledContainer>
            <LoadingIndicator isLoading={isLoading || isIssuing} message={t('common.loading')} />
            <DownloadIndicator ref={downloadIndicatorRef} />

            <CustomAlert
                open={alertInfo.open}
                title={alertInfo.title}
                message={alertInfo.message}
                customContent={alertInfo.customContent}
                severity={alertInfo.severity}
                onConfirm={alertInfo.onConfirm || handleCloseAlert}
                onClose={alertInfo.onClose || handleCloseAlert}
                isConfirm={alertInfo.isConfirm === true}
                confirmText={alertInfo.confirmText}
                cancelText={alertInfo.cancelText}
            />

            <FilterSection
                searchParams={searchParams}
                handleParamChange={handleParamChange}
                handleSearch={handleSearch}
                handleReset={handleReset}
                isLoading={isLoading}
            />

            <Box sx={{ width: '100%' }}>
                <TableControls
                    t={t}
                    showMyOrderOnly={showMyOrderOnly}
                    setShowMyOrderOnly={(checked) => {
                        setShowMyOrderOnly(checked);
                        showMyOrderOnlyRef.current = checked;
                        setPage(0);
                        if (hasSearched) {
                            setNeedToFetch(true);
                        }
                    }}
                    hideInvoicedRows={hideInvoicedRows}
                    setHideInvoicedRows={(checked) => {
                        setHideInvoicedRows(checked);
                        hideInvoicedRowsRef.current = checked;
                        setPage(0);
                        if (hasSearched) {
                            setNeedToFetch(true);
                        }
                    }}
                    toggleColumnPanel={toggleColumnPanel}
                    columnPanelOpen={columnPanelOpen}
                    columnsToUse={columnsToUse}
                    checkedColumns={checkedColumns}
                    handleCheckColumn={handleCheckColumn}
                    selectAllColumns={selectAllColumns}
                    unselectAllColumns={unselectAllColumns}
                    selectedItems={selectedItems}
                    onInvoiceIssuance={handleInvoiceIssuance}
                    onDebitNoteExport={handleDebitNoteExport}
                    summary={displaySummary}
                    hasSearched={hasSearched}
                    loadedCount={invoiceData.length}
                    totalCount={totalCount}
                    hasMore={hasMore}
                />

                {isMobile ? (
                    <InvoiceMobileView
                        invoiceData={invoiceData}
                        selectedItems={selectedItems}
                        onSelectItem={handleSelectItem}
                        onSubOrderClick={handleSubOrderClick}
                        isInvoiceNoLoading={isInvoiceNoLoading}
                        onInvoicePreviewById={handleInvoicePreviewById}
                        hasMore={hasMore}
                        hasSearched={hasSearched}
                        isLoading={isLoading}
                        loadingMore={isLoadingMore}
                        onLoadMore={loadMore}
                        t={t}
                    />
                ) : (
                    <InvoiceTable
                        invoiceData={invoiceData}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={totalCount}
                        isLoading={isLoading}
                        isPackingLoading={isPackingLoading}
                        isGiPostLoading={isGiPostLoading}
                        isInvoiceNoLoading={isInvoiceNoLoading}
                        loadingMore={isLoadingMore}
                        hasMore={hasMore}
                        onLoadMore={loadMore}
                        columnsToUse={columnsToUse}
                        checkedColumns={checkedColumns}
                        selectedItems={selectedItems}
                        onSelectItem={handleSelectItem}
                        onSelectAll={handleSelectAll}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        onSubOrderClick={handleSubOrderClick}
                        onInvoicePreviewById={handleInvoicePreviewById}
                        sortConfig={invoiceTableSortConfig}
                        onSortConfigChange={setInvoiceTableSortConfig}
                        hasSearched={hasSearched}
                        t={t}
                    />
                )}
            </Box>

            <InvoiceIssuanceDialog
                open={invoiceDialogOpen}
                onClose={() => setInvoiceDialogOpen(false)}
                onConfirm={handleInvoiceConfirm}
                isSubmitting={isIssuing}
                selectedItems={selectedItems}
                invoiceData={invoiceData}
                t={t}
            />

            <SubOrderDetailDialog
                open={subOrderDetailDialogOpen}
                onClose={handleCloseSubOrderDialog}
                subOrderId={selectedSubOrderId}
            />

            <InvoicePrintModal
                open={invoicePreviewOpen}
                onClose={() => setInvoicePreviewOpen(false)}
                selectedInvoices={invoicePreviewSelectedIds}
                invoiceData={invoicePreviewStubRows}
                initialColumns={printCheckedColumns}
            />
        </StyledContainer>
    );
};

export default InvoiceIssuance;
